/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  ArrowRight,
  Check,
  Mic2,
  Pause,
  Play,
  RotateCcw,
  SlidersHorizontal,
  Sparkles,
  Volume2,
} from 'lucide-react';
import ParticleBackground from './ParticleBackground';
import AuraButton from './AuraButton';

interface HeroProps {
  scrollToSection: (id: string) => void;
}

const taglines = [
  'あなたの日常に、新しいメロディを',
  '歌声で、もっと深くつながる',
  'カラオケの楽しさに、新しい体験を',
  'ひとりの歌から、みんなの歌へ',
];

const lyrics = [
  '君の日常に 新しいメロディを',
  '歌声でつながろう もっと深く',
  'いつでも どこでも 歌いたい時に',
  'お気に入りの曲を見つけて',
  'みんなと一緒に盛り上がろう',
];

const midiNotes = [
  { top: 16, width: 52, delay: 0, duration: 4.8 },
  { top: 36, width: 34, delay: 0.8, duration: 4.3 },
  { top: 57, width: 64, delay: 1.5, duration: 5.1 },
  { top: 78, width: 42, delay: 2.2, duration: 4.6 },
  { top: 26, width: 48, delay: 3, duration: 4.9 },
  { top: 67, width: 30, delay: 3.7, duration: 4.2 },
];

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
  const secs = (seconds % 60).toString().padStart(2, '0');
  return `${mins}:${secs}`;
};

export default function Hero({ scrollToSection }: HeroProps) {
  const [taglineIndex, setTaglineIndex] = useState(0);
  const [taglineText, setTaglineText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [typingReady, setTypingReady] = useState(false);
  const [activeLyricIndex, setActiveLyricIndex] = useState(2);
  const [isPlaying, setIsPlaying] = useState(true);
  const [elapsed, setElapsed] = useState(28);
  const [activeControl, setActiveControl] = useState('pause');

  useEffect(() => {
    const timer = window.setTimeout(() => setTypingReady(true), 750);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!typingReady) return;
    const fullText = taglines[taglineIndex];
    let timer: number;

    if (isTyping && taglineText.length < fullText.length) {
      timer = window.setTimeout(
        () => setTaglineText(fullText.slice(0, taglineText.length + 1)),
        72,
      );
    } else if (isTyping) {
      timer = window.setTimeout(() => setIsTyping(false), 2300);
    } else if (taglineText.length > 0) {
      timer = window.setTimeout(
        () => setTaglineText(taglineText.slice(0, -1)),
        32,
      );
    } else {
      setTaglineIndex((previous) => (previous + 1) % taglines.length);
      setIsTyping(true);
    }

    return () => window.clearTimeout(timer);
  }, [isTyping, taglineIndex, taglineText, typingReady]);

  useEffect(() => {
    if (!isPlaying) return;
    const lyricTimer = window.setInterval(
      () => setActiveLyricIndex((previous) => (previous + 1) % lyrics.length),
      3500,
    );
    const timeTimer = window.setInterval(() => setElapsed((previous) => previous + 1), 1000);
    return () => {
      window.clearInterval(lyricTimer);
      window.clearInterval(timeTimer);
    };
  }, [isPlaying]);

  const togglePlayback = () => {
    setIsPlaying((previous) => !previous);
    setActiveControl('pause');
  };

  const lyricAt = (offset: number) => {
    const index = (activeLyricIndex + offset + lyrics.length) % lyrics.length;
    return lyrics[index];
  };

  const controlClass = (id: string) =>
    `w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${
      activeControl === id
        ? 'bg-[#00F5D4] text-[#03110F] shadow-[0_0_18px_rgba(0,245,212,0.45)]'
        : 'bg-white/6 text-white/65 hover:bg-white/10 hover:text-white'
    }`;

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center pt-24 overflow-hidden bg-[#080A0A]"
    >
      <ParticleBackground />
      <div className="absolute inset-0 z-0 pointer-events-none select-none">
        <motion.div
          className="absolute top-[18%] left-[42%] w-[560px] h-[560px] rounded-full bg-[#00F5D4]/10 blur-[130px]"
          animate={{ opacity: [0.45, 0.8, 0.45], scale: [0.92, 1.08, 0.92] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="absolute bottom-[5%] right-[2%] w-[380px] h-[380px] rounded-full bg-[#6B5CFF]/7 blur-[120px]" />
      </div>

      <div className="w-full max-w-7xl mx-auto px-5 md:px-8 z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center py-10 lg:py-12">
        <div className="lg:col-span-7 flex flex-col items-start text-left">
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#00F5D4]/10 border border-[#00F5D4]/20 text-xs font-semibold text-[#00F5D4] mb-6"
          >
            <Sparkles className="w-3.5 h-3.5 animate-pulse" />
            2026年秋、正式リリース予定
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.12 }}
            className="text-5xl md:text-6xl lg:text-7xl font-black tracking-[-0.04em] leading-none text-white"
          >
            Koemori
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.6 }}
            className="h-12 md:h-16 mt-4 mb-5 flex items-center"
            aria-live="polite"
          >
            <span className="text-xl md:text-3xl lg:text-[34px] font-bold bg-gradient-to-r from-[#00F5D4] via-[#54FFD9] to-[#55B9FF] bg-clip-text text-transparent">
              {taglineText}
            </span>
            <span className="w-[3px] h-7 md:h-9 ml-1 bg-[#00F5D4] animate-pulse" />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-base md:text-lg text-white/65 max-w-xl leading-8 mb-9"
          >
            いつでも、どこでも、歌いたい瞬間に。 公式のハイクオリティ伴奏と精密なボーカル分析で、あなたの歌声をもっと自由に、もっと美しく。

          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.42 }}
            className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto"
          >
            <div className="relative w-full sm:w-auto">

              <AuraButton
                onClick={() => scrollToSection('download')}
                className="w-full sm:w-auto"
                variant="primary"
              >
                今すぐ体験
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </AuraButton>
            </div>
            <button
              onClick={() => scrollToSection('features')}
              className="px-8 py-4 rounded-full bg-white/5 backdrop-blur-md border border-white/10 text-white font-semibold hover:bg-white/10 hover:border-white/20 transition-all duration-300 cursor-pointer w-full sm:w-auto"
            >
              詳細を見る
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.85, ease: 'easeOut' }}
          className="lg:col-span-5 flex justify-center lg:justify-end"
        >
          <motion.div
            animate={{ y: [0, -10, 0], rotate: [-0.7, 0.8, -0.7] }}
            transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut' }}
            className="relative w-[310px] h-[650px] md:w-[326px] md:h-[674px] bg-[#171919] rounded-[50px] p-[10px] border border-white/15 shadow-[0_30px_80px_rgba(0,0,0,0.75),0_0_55px_rgba(0,245,212,0.16)]"
          >
            <div className="absolute top-[17px] left-1/2 -translate-x-1/2 w-[112px] h-[25px] bg-black rounded-full z-30 flex items-center justify-center">
              <span className="w-2 h-2 rounded-full bg-[#172F2A] shadow-[0_0_5px_rgba(0,245,212,0.45)]" />
            </div>

            <div className="h-full rounded-[40px] overflow-hidden relative bg-[#101010]">
              <img
                src="/assets/hero-home.webp"
                alt="Koemoriホーム画面"
                fetchPriority="high"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/20 pointer-events-none" />

              <div className="absolute left-0 right-0 top-[44%] bottom-[22%] overflow-hidden pointer-events-none">
                {[
                  { top: '22%', startX: 280, delay: 0 },
                  { top: '30%', startX: 280, delay: 2.4 },
                  { top: '62%', startX: 280, delay: 4.8 },
                  { top: '70%', startX: 280, delay: 7.2 },
                ].map((item, index) => (
                  <motion.img
                    key={index}
                    src="/assets/hero-danmaku.webp"
                    alt=""
                    aria-hidden="true"
                    className="absolute w-[76px] max-w-none drop-shadow-[0_0_12px_rgba(0,245,212,0.25)]"
                    style={{ top: item.top }}
                    initial={{ x: item.startX, opacity: 0 }}
                    animate={{ x: -260, opacity: [0, 1, 1, 0] }}
                    transition={{ duration: 8, delay: item.delay, repeat: Infinity, repeatDelay: 2, ease: 'linear' }}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
